import { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { getSessionCurrencyTypeId } from 'app/api/cookie';
import { useGetUser, useMultiCompanyDetails } from 'app/api/hooks/core/v1/auth/user';
import ThemeMenuItem from 'app/components/layout/AppLayout/DesktopNav/AccountMenu/ThemeMenuItem';
import Logout from 'app/components/layout/AppLayout/Logout';
import UserInfo from 'app/components/layout/AppLayout/UserInfo';
import Box from 'app/components/v2/components/Box';
import Button from 'app/components/v2/components/Button';
import Divider from 'app/components/v2/components/Divider';
import Icon from 'app/components/v2/components/Icon';
import { TooltipProps } from 'app/components/v2/components/Tooltip';
import TooltipIcon from 'app/components/v2/components/TooltipIcon';
import { keys } from 'app/config/keys';
import {
  externalRoutes,
  factorRoutes,
  payeeRoutes,
  payorRoutes,
  routes,
} from 'app/constants/routes';
import { CompanyTypeId } from 'app/constants/user';
import { useHasMultiCurrency } from 'app/hooks/payee/useHasMultiCurrency';
import { useCanUseDarkMode } from 'app/hooks/useUiTheme';
import { useAlertsDispatch } from 'app/state/AlertsContext/hooks';
import { useAuthState, useIsNoaIbex } from 'app/state/AuthContext/hooks';
import { buildJumpLink } from 'app/utils/lib/routing';
import { copyToClipboard } from 'app/utils/lib/string';
import { isTriumphInternalOrAppManager } from 'app/utils/lib/user';

import AccountMenuItem from './AccountMenuItem';
import CompanyJumper from './CompanyJumper';
import Menu from './Menu';
import MultiCurrencyMenuItem from './MultiCurrencyMenuItem';

// Local Typings
interface NavItem {
  label: string;
  path: string;
}

type ExternalUserCompanyTypeId = CompanyTypeId.Factor | CompanyTypeId.Payee | CompanyTypeId.Payor;

interface Props {
  shouldRenderCompanyName: boolean;
  shouldRenderCompanyType?: boolean;
}

// Local Variables
const changeLocation = (path: string) => () => {
  window.location.href = path;
};

const sessionCurrencyTypeId = getSessionCurrencyTypeId();

// Component Definition
const AccountMenu: FC<Props> = props => {
  const { t } = useTranslation('components');
  const { authRoles } = useAuthState();
  const user = useGetUser();
  const multiCompanyDetails = useMultiCompanyDetails();

  const [isOpen, setIsOpen] = useState(false);

  const navigate = useNavigate();

  const { hasMultiCurrency } = useHasMultiCurrency();

  const userIsAdmin = isTriumphInternalOrAppManager(authRoles);

  const isNoaProcessorOnly = useIsNoaIbex();
  const shouldShowMultiCurrency = !isNoaProcessorOnly && (hasMultiCurrency || !!sessionCurrencyTypeId);
  const showCompanyJumper = userIsAdmin || multiCompanyDetails.hasMultipleCompanies;

  const handleClose = (cb?: () => void) => () => {
    setIsOpen(false);
    if (cb) {
      cb();
    }
  };

  const handleOpen = useCallback(
    () => {
      setIsOpen(true);
    },
    [],
  );

  const myProfileLabel: string = t('accountMenu.myProfile', 'My Profile');

  const navItems: Record<ExternalUserCompanyTypeId, NavItem[]> = {
    [CompanyTypeId.Factor]: [
      {
        label: myProfileLabel,
        path: `/${routes.factor}/${factorRoutes.profile}`,
      },
    ],
    [CompanyTypeId.Payee]: [
      {
        label: myProfileLabel,
        path: `/${routes.payee}/${payeeRoutes.profile}`,
      },
    ],
    [CompanyTypeId.Payor]: [
      {
        label: myProfileLabel,
        path: `/${routes.payor}/${payorRoutes.profile}`,
      },
    ],
  };

  const alertsDispatch = useAlertsDispatch();

  const handleCopyUrl = useCallback(() => {
    copyToClipboard(buildJumpLink((user?.company?.id ?? ''),
      window.location.pathname));
    alertsDispatch({
      payload: {
        alert: {
          actions: [],
          alertLevel: 'success',
          alertType: 'notification',
          message: t('accountMenu.copyUrlSuccessMessage', 'Copied link to current page'),
          timestamp: Date.now(),
          title: t('common:successExclaim', 'Success!'),
        },
      },
      type: 'addAlert',
    });
  },
  [user?.company?.id, alertsDispatch, t]);

  const copyLinkTooltipProps: Omit<TooltipProps, 'childRef' | 'children'> = {
    alignment: 'left',
    noWrap: true,
    tooltipLabel: 'Internal use only',
    tooltipPlacement: 'right',
  };
  const copyLinkLabel = (
    <Box
      alignItems="center"
      display="flex"
      gap={1}
    >
      {t('accountMenu.copyJumpLink', 'Copy Jump Link')}
      <TooltipIcon tooltipProps={copyLinkTooltipProps} />
    </Box>
  );
  const redirectTo = (path: string) => () => {
    navigate(path);
  };
  const copyIcon = useMemo(() =>
    <Icon name="link" />,
  []);

  const canUseDarkMode = useCanUseDarkMode();

  function renderSubMenu() {
    if (user?.company) {
      return (
        <ul>
          {userIsAdmin && (
            <AccountMenuItem
              label={t('common:admin', 'Admin')}
              onClick={handleClose(changeLocation(`${keys.CORE_URL}/${externalRoutes.admin}`))}
            />
          )}

          {!isNoaProcessorOnly
            ? navItems[user.company.type.id as ExternalUserCompanyTypeId]?.map(item => (
              <AccountMenuItem
                key={item.label}
                label={item.label}
                onClick={handleClose(redirectTo(item.path))}
              />
            ))
            : null}

          {shouldShowMultiCurrency && (
            <MultiCurrencyMenuItem />
          )}

          {userIsAdmin && (
            <AccountMenuItem
              label={copyLinkLabel}
              menuIcon={copyIcon}
              onClick={handleCopyUrl}
            />
          )}

          {canUseDarkMode && (
            <ThemeMenuItem />
          )}
        </ul>
      );
    }
  }

  return (
    <Box
      alignItems="center"
      display="flex"
      position="relative"
      zIndex={150}
    >
      <UserInfo
        alignment="right"
        hideCompanyType={!props.shouldRenderCompanyType}
        hideUserInfo={!props.shouldRenderCompanyName}
        isOpen={isOpen}
        onIconClick={handleOpen}
        textColor="invert"
      />

      <Menu
        isOpen={isOpen}
        onClose={handleClose()}
        width={350}
      >
        <UserInfo
          alignment="right"
          isOpen={isOpen}
          withMarginBottom
        />

        {showCompanyJumper && (
          <>
            <CompanyJumper />

            <Divider marginY={0} />
          </>
        )}

        {renderSubMenu()}

        <Box marginTop={4}>
          <Logout>
            {({ onLogout }) => (
              <Box
                alignItems="center"
                display="flex"
                justifyContent="center"
              >
                <Button
                  hierarchy="tertiary"
                  onClick={handleClose(onLogout)}
                  rightIcon="exit"
                >
                  {t('accountMenu.signOutLink', 'Sign out')}
                </Button>
              </Box>
            )}
          </Logout>
        </Box>
      </Menu>
    </Box>
  );
};

export default AccountMenu;
